import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../utils/api';

const Step = ({ number, title, description, screenshot }) => (
  <div className="mb-8">
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-500 text-white flex items-center justify-center font-bold text-lg">
        {number}
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 leading-relaxed">{description}</p>
        {screenshot && (
          <div className="mt-4 rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <img src={screenshot} alt={title} className="w-full" />
          </div>
        )}
      </div>
    </div>
  </div>
);

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        <svg
          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="pb-4 text-gray-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const StudentGuidePage = () => {
  const { slug } = useParams();
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudio = async () => {
      try {
        const { data } = await api.get(`/widget/${slug}/courses`);
        setStudio(data.studio);
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchStudio();
  }, [slug]);

  // Set page title + favicon
  useEffect(() => {
    if (studio?.name) {
      document.title = `Student Guide — ${studio.name}`;
    }
    if (studio?.branding?.logo) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = studio.branding.logo;
      document.head.appendChild(link);
    }
  }, [studio]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  const studioName = studio?.name || 'Your Studio';
  const loginUrl = `https://studio.mydancehall.com/s/${slug}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center gap-4">
          {studio?.branding?.logo ? (
            <img src={studio.branding.logo} alt={studioName} className="h-12 object-contain" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center">
              <span className="text-xl font-bold text-brand-600">{studioName.charAt(0)}</span>
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900">{studioName}</h1>
            <p className="text-sm text-gray-500">Student Guide</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">

        {/* Welcome */}
        <div className="bg-brand-50 rounded-2xl p-8 mb-10 border border-brand-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Welcome to {studioName}!</h2>
          <p className="text-gray-700 leading-relaxed">
            This guide will walk you through how to sign up for classes, log in to your account,
            access class materials, and manage your profile. Follow the steps below to get started.
          </p>
        </div>

        {/* Section: How to Sign Up */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </span>
            How to Sign Up
          </h2>

          <Step
            number={1}
            title="Click your enrollment link"
            description="Your teacher will share an enrollment link for your specific class. Click the link to see the course details."
            screenshot="/images/guide/step1-course-details.png"
          />
          <Step
            number={2}
            title="Choose your billing plan"
            description="Select either Monthly or Quarterly billing. The quarterly option usually offers a discount."
          />
          <Step
            number={3}
            title="Click Log In"
            description="Scroll down and click Log In. Enter the email and password you received from your teacher or via email."
            screenshot="/images/guide/step2-signup-buttons.png"
          />
          <Step
            number={4}
            title="Enter your email and password"
            description="Enter the email and password you got in email or your teacher shared with you."
            screenshot="/images/guide/step3-login-screen.png"
          />
          <Step
            number={5}
            title="Complete payment"
            description="Choose Bank as your payment method, enter your bank details, and click the Enroll & Pay button."
            screenshot="/images/guide/step4-payment.png"
          />
          <Step
            number={6}
            title="You're enrolled!"
            description={"After payment, click \"Go to Dashboard\" to see your classes and materials. You can also log in anytime at " + loginUrl.replace('https://', '') + "."}
          />
        </div>

        {/* Section: How to Log In */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
            </span>
            How to Log In
          </h2>

          <Step
            number={1}
            title="Go to your studio's login page"
            description={
              <>
                Open your browser and go to{' '}
                <a href={loginUrl} target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:text-brand-600 font-medium underline">
                  {loginUrl.replace('https://', '')}
                </a>
              </>
            }
          />
          <Step
            number={2}
            title='Click "Sign In"'
            description="Click the Sign In button on the studio page."
          />
          <Step
            number={3}
            title="Enter your email and password"
            description="Type in the email and password you used when you created your account. Then click Continue."
            screenshot="/images/guide/step3-login-screen.png"
          />
          <Step
            number={4}
            title="You're in!"
            description="You'll be taken to your student dashboard where you can see your enrolled classes and materials."
          />
        </div>

        {/* Section: Reset Password */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </span>
            Forgot Your Password?
          </h2>

          <Step
            number={1}
            title="Go to the login page"
            description={
              <>
                Open{' '}
                <a href={loginUrl} target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:text-brand-600 font-medium underline">
                  {loginUrl.replace('https://', '')}
                </a>
              </>
            }
          />
          <Step
            number={2}
            title="Click 'Forgot your password?' and enter your email"
            description="Click the link below the Sign In button, enter your email address, and click Send."
            screenshot="/images/guide/step5-forgot-password.png"
          />
          <Step
            number={3}
            title="Check your inbox"
            description={`You'll receive an email from ${studioName} with a link to reset your password. Check your spam/junk folder if you don't see it.`}
          />
          <Step
            number={4}
            title="Set a new password"
            description="Click the link in the email and choose a new password. You can then log in with your new password."
          />
        </div>

        {/* FAQs */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
            </span>
            Frequently Asked Questions
          </h2>

          <div className="bg-white rounded-xl border border-gray-100 px-6">
            <FAQItem
              question="I didn't receive a confirmation email after signing up"
              answer="Check your spam or junk folder. Emails come from the studio's notification address. If you still can't find it, try logging in directly — your account may already be active."
            />
            <FAQItem
              question="How do I change my password?"
              answer={"Log in to your dashboard, go to \"Profile\" in the sidebar, and click \"Send Password Reset Email\". You'll receive an email with a link to set a new password."}
            />
<FAQItem
              question="How do I watch class videos?"
              answer={"Log in to your dashboard and go to \"My Courses\". Click on your course and then \"Materials\" to see videos your teacher has released."}
            />
<FAQItem
              question="How do I update my payment method?"
              answer="Contact your teacher directly to update your payment details."
            />
            <FAQItem
              question="How do I cancel my enrollment?"
              answer="If you plan to discontinue classes with TFA TX, please inform us at least 31 days before your next billing cycle. The enrollment 'last month' fee paid at registration will be applied toward your final month of classes. For example, if your last class date is April 1, please notify us by February 28. The March class fee will be covered by your enrollment 'last month' fee. Please note that this fee is non-refundable, and failure to provide the required notice will result in forfeiture of the fee."
            />
            <FAQItem
              question="What is the attendance policy?"
              answer="A minimum attendance of 70% is expected for dancers to remain in good standing and to be eligible for exams. If you or your dancer need to miss a class, please inform us ahead of time with a valid reason. Unforeseen situations will be reviewed on a case-by-case basis. In the event of a missed class, it is the dancer's responsibility to catch up on the material covered using the class recordings or reference videos provided. Teachers are always happy to assist with questions or clarifications."
            />
            <FAQItem
              question="I'm having trouble logging in"
              answer={"Try resetting your password using the \"Forgot your password?\" link on the login page. Make sure you're using the same email address you signed up with."}
            />
            <FAQItem
              question="What time zone are class times in?"
              answer="All class times are listed in Central Time (CT)."
            />
          </div>
        </div>

        {/* Back to login */}
        <div className="text-center pb-10">
          <Link
            to={`/s/${slug}`}
            className="inline-flex items-center gap-2 bg-brand-500 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-brand-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
            </svg>
            Go to Sign In
          </Link>
          <p className="text-xs text-gray-400 mt-6">
            Powered by <a href="https://mydancehall.com" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-500">DanceHall</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentGuidePage;
